import { createContext } from 'react';
import type { PostHog } from 'posthog-js/react';
import type { PartialFlagAtlas } from '../flags';
import posthog from 'posthog-js';

// The environment the user is in, where `unknown` represents a failure
// to determine the environment. And should fallback to the safe default.
export type FeatureFlagEnvironment = 'local' | 'staging' | 'production' | 'unknown';

/**
 * Converts tenant context environment to flag provider environment.
 * Maps 'development' and 'test' to 'local', and validates other environments.
 */
export function toFlagEnvironment(env: string | undefined): FeatureFlagEnvironment {
  if (env === 'production' || env === 'staging' || env === 'local') {
    return env;
  }
  if (env === 'development' || env === 'test') {
    return 'local';
  }
  return 'unknown';
}

/**
 * Represents the data required about a user to be sent when
 * evaluating a feature flag. It's only a subset of a user's
 * data.
 */
export type FeatureFlagEvaluationData = {
  isIdentified: boolean;
  user:
    | {
        drn: string | undefined;
      }
    | undefined
    | null;
  environment: FeatureFlagEnvironment;
  initialFlags: PartialFlagAtlas;
  posthog: PostHog;
  debug?: boolean;
};

/**
 * Extended context type that includes the refetch function for manual refreshes.
 */
export type FeatureFlagContextValue = FeatureFlagEvaluationData & {
  /**
   * Manually trigger a flag refresh.
   * Only works if `flagsQueryResult` was provided to the provider.
   */
  refetchFlags: () => void;
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  isIdentified: false,
  user: undefined,
  environment: 'unknown',
  initialFlags: {},
  posthog,
  refetchFlags: () => {},
});
